<script setup lang="ts">
const { loadNavigationElements } = useNavigation()
const { data } = useAsyncData("navigation", () => {
    return loadNavigationElements({
        depth: 3,
        includes: {
            category: [
                'id',
                'name',
                'children',
                'level',
                'seoUrls',
                'customFields',
                'active',
                'type',
                'externalLink',
                'internalLink',
                'linkType',
            ],
            seo_url: [
                'pathInfo',
                'seoPathInfo',
            ],
        },
    })
})
provide('mainNavigation', data)

const { totalProductQuantity } = useCart()

const { count: wishlistCount } = useWishlist()

const { count: clickAndReserveCount } = useClickAndReserve()

const { isLoggedIn } = useUser()

const { open } = useSidenav()

const { closePopover, clearPopoverTimeout } = useMegaMenu()
</script>

<template>
    <!-- Skip Link -->
    <LayoutSkipLinks />

    <!-- Header -->
    <header @mouseleave="closePopover({ delay: 300 })">
        <div class="relative border-b border-border z-60 bg-base-100">
            <div class="m-auto w-full max-w-8xl px-2">
                <div class="flex gap-x-2 flex-wrap justify-between items-center">
                    <div class="flex justify-between items-center gap-1 order-20 mr-auto xl:gap-8 py-2 lg:py-0">
                        <SidenavMenu />

                        <FoundationLink href="/" class="block w-full max-w-[75px] lg:max-w-[115px] order-20 mr-auto lg:mr-0">
                            <span class="sr-only">{{ $t('header.homeLink') }}</span> <LayoutLogo />
                        </FoundationLink>

                        <div class="order-30 hidden xl:block" @mouseleave="closePopover({ delay: 300 })">
                            <MegaMenu />
                        </div>
                    </div>

                    <div class="shrink-0 flex justify-between items-center gap-2 xl:gap-4 order-30 lg:order-40 py-2 xl:py-0">
                        <FoundationButton
                            size="small"
                            color="primary"
                            class="relative gap-1 border-0 h-[34px] min-h-[34px] rounded-full max-w-[80px] px-[8px] py-[6px] text-left"
                            :aria-label="$t('header.clickAndReserve.open')"
                            @click="open('clickAndReserve')"
                        >
                            <FoundationIcon name="map-pin" class="w-[20px]" />

                            <span class="text-[10px] leading-[9px] flex-1">{{ $t('header.clickAndReserve.open') }}</span>

                            <client-only>
                                <span
                                    v-if="clickAndReserveCount > 0"
                                    class="absolute bottom-[2px] right-[2px] bg-primary text-primary-content text-[10px] font-normal rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                                    :aria-label="$t('header.clickAndReserve.itemCount', { count: clickAndReserveCount })"
                                >
                                    {{ clickAndReserveCount > 99 ? '99+' : clickAndReserveCount }}
                                </span>
                            </client-only>
                        </FoundationButton>
                        <FoundationButton
                            size="medium"
                            variant="ghost"
                            circle
                            class="relative"
                            :aria-label="$t('header.wishlist.open')"
                            @click="open('wishlist')"
                        >
                            <span class="sr-only">{{ $t('header.wishlist.open') }}</span><FoundationIcon name="heart" />
                            <client-only>
                                <span
                                    v-if="wishlistCount > 0"
                                    class="absolute bottom-[2px] right-[2px] bg-primary text-primary-content text-[10px] font-normal rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                                    :aria-label="$t('header.wishlist.itemCount', { count: wishlistCount })"
                                >
                                    {{ wishlistCount > 99 ? '99+' : wishlistCount }}
                                </span>
                            </client-only>
                        </FoundationButton>
                        <client-only>
                            <!-- Show account sidenav button if logged in -->
                            <FoundationButton
                                v-if="isLoggedIn"
                                size="medium"
                                variant="ghost"
                                square
                                class="relative"
                                :aria-label="$t('header.accountLink')"
                                @click="open('account')"
                            >
                                <span class="sr-only">{{ $t('header.accountLink') }}</span> <FoundationIcon name="user-check" />
                            </FoundationButton>

                            <!-- Show login sidenav button if not logged in -->
                            <FoundationButton
                                v-else
                                size="medium"
                                variant="ghost"
                                circle
                                :aria-label="$t('header.account.open')"
                                @click="open('account')"
                            >
                                <span class="sr-only">{{ $t('header.account.open') }}</span><FoundationIcon name="user" />
                            </FoundationButton>

                            <template #fallback>
                                <!-- Default to login button on server -->
                                <FoundationButton
                                    size="medium"
                                    variant="ghost"
                                    circle
                                    :aria-label="$t('header.account.open')"
                                    @click="open('account')"
                                >
                                    <span class="sr-only">{{ $t('header.account.open') }}</span><FoundationIcon name="user" />
                                </FoundationButton>
                            </template>
                        </client-only>
                        <FoundationButton
                            size="medium"
                            variant="ghost"
                            circle
                            class="relative"
                            :aria-label="$t('header.cart.open')"
                            @click="open('cart')"
                        >
                            <span class="sr-only">{{ $t('header.cart.open') }}</span><FoundationIcon name="cart" />
                            <client-only>
                                <span
                                    v-if="totalProductQuantity > 0"
                                    class="absolute bottom-[2px] right-[2px] bg-primary text-primary-content text-[10px] font-normal rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                                    :aria-label="$t('header.cart.itemCount', { count: totalProductQuantity })"
                                >
                                    {{ totalProductQuantity > 99 ? '99+' : totalProductQuantity }}
                                </span>
                            </client-only>
                        </FoundationButton>
                    </div>

                    <SearchQuick class="order-40 xl:order-30 xl:max-w-[210px] xl:my-0 -mx-2 xl:mx-0 w-[calc(100%+16px)] xl:w-full border-t border-border xl:border-0 p-2 xl:p-0" />
                </div>
            </div>
        </div>
        <div class="relative z-50" @mouseenter="clearPopoverTimeout()">
            <MegaMenuFlyout />
        </div>

        <MegaMenuSubNavigation />
    </header>

    <main>
        <slot />
        <MiscScrollToTopButton />
    </main>

    <!-- Cart Sidenav -->
    <SidenavCart />

    <!-- Wishlist Sidenav -->
    <SidenavWishlist />

    <!-- Click & Reserve Sidenav -->
    <SidenavClickAndReserve />

    <!-- Account Sidenav -->
    <SidenavAccount />

    <!-- Global Notifications -->
    <NotificationContainer />

    <!-- Footer -->
    <LayoutFooter :depth-footer-navigation="1" :depth-service-navigation="1" />
</template>
